'use client';

import {
  AnimatedDiv,
  checkIsNative,
  cn,
  Div,
  H3,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
  useHandleBack,
  useKeyboardShown,
  useSafeAreaInsets,
} from '@scatterlab/zeta-core';
import { Transition } from 'framer-motion';
import { FC, memo, ReactNode, useLayoutEffect, useMemo } from 'react';

import IconButton from './button/IconButton';
import useOverflowHidden from './hooks/useOverflowHidden';
import useReposition from './hooks/useReposition';
import { Delete16Icon, Previous16Icon } from './icons';

export type ModalProps = {
  onPressClose?: () => void;
  onPressBackdrop?: () => void;
  onPressPrevious?: () => void;
  title?: string;
  children?: ReactNode;
  containerClassName?: string;
  className?: string;
  headerClassName?: string;
  contentContainerClassName?: string;
  targetElementId?: string;
  backdropVariant: 'blur' | 'opacity';
  backdropClassName?: string;
  mobileViewVariant?: 'fullScreen' | 'bottomSheet' | 'center';
  titleClassName?: string;
  rightElement?: ReactNode;
  leftElement?: ReactNode;
  isHeaderHidden?: boolean;
  top?: number;
  scrollable?: boolean;
  ignoreSafeArea?: boolean;
  ignoreBottomSafeArea?: boolean;
  backgroundImage?: string;
  transition?: Transition;
};

const INSET_ZERO = { top: 0, bottom: 0, left: 0, right: 0 } as const;

const Modal: FC<ModalProps> = ({
  onPressClose,
  onPressBackdrop,
  onPressPrevious,
  title,
  children,
  containerClassName,
  className,
  headerClassName,
  contentContainerClassName,
  targetElementId,
  backdropClassName,
  backdropVariant,
  mobileViewVariant = 'bottomSheet',
  titleClassName,
  rightElement,
  leftElement,
  isHeaderHidden,
  top: topAsProp = 0,
  scrollable,
  ignoreSafeArea,
  ignoreBottomSafeArea,
  backgroundImage,
  transition,
}) => {
  const { top, bottom } = useSafeAreaInsets();
  const keyboardShown = useKeyboardShown();

  const { isFullScreen, isBottomSheet, isCenter } = useMemo(
    () => ({
      isFullScreen: mobileViewVariant === 'fullScreen',
      isBottomSheet: mobileViewVariant === 'bottomSheet',
      isCenter: mobileViewVariant === 'center',
    }),
    [mobileViewVariant],
  );

  useOverflowHidden(true, targetElementId);
  useHandleBack(onPressClose || onPressBackdrop);

  useLayoutEffect(() => {
    Keyboard.dismiss();
  }, []);

  const position = useReposition(targetElementId);

  const modalContents = useMemo(() => {
    let nativeBottomPadding;
    if (!ignoreBottomSafeArea && checkIsNative())
      nativeBottomPadding = (keyboardShown ? 0 : bottom) + 16;

    if (scrollable) {
      return (
        <ScrollView
          className={cn('flex flex-col overflow-y-auto px-4 pb-4', className)}
          style={{ paddingBottom: nativeBottomPadding }}
          contentContainerClassName={cn('grow', contentContainerClassName)}
        >
          {children}
        </ScrollView>
      );
    }

    return (
      <Div
        className={cn('flex flex-col px-4 pb-4', className)}
        style={{ paddingBottom: nativeBottomPadding }}
      >
        {children}
      </Div>
    );
  }, [
    ignoreBottomSafeArea,
    keyboardShown,
    bottom,
    scrollable,
    className,
    children,
    contentContainerClassName,
  ]);

  const TopComponent = scrollable ? Div : KeyboardAvoidingView;

  return (
    <TopComponent
      behavior="padding"
      className="absolute z-50 flex flex-1 flex-col items-center justify-center"
      style={{
        ...position,
        paddingTop: (ignoreSafeArea ? 0 : top) + topAsProp,
      }}
    >
      <AnimatedDiv
        role="presentation"
        className={cn(
          'absolute inset-0 -z-10',
          {
            'backdrop-blur-[8px] bg-black/80': backdropVariant === 'blur',
            'bg-black/60 ': backdropVariant === 'opacity',
          },
          backdropClassName,
        )}
        style={INSET_ZERO}
        onPress={onPressBackdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <AnimatedDiv
        className={cn(
          'relative flex max-h-full w-auto max-w-full flex-col rounded-xl bg-gray-sub1 shadow-400',
          containerClassName,
          {
            'mt-auto w-full rounded-b-none': isBottomSheet,
            'h-full w-full rounded-none bg-gray-main': isFullScreen,
            'mx-8': isCenter,
          },
        )}
        initial={{
          opacity: 0,
          ...(isBottomSheet ? { y: '100%' } : {}),
        }}
        animate={{
          opacity: 1,
          ...(isBottomSheet ? { y: '0%' } : {}),
        }}
        exit={{
          opacity: 0,
          ...(isBottomSheet ? { y: '100%' } : {}),
        }}
        transition={transition}
      >
        {backgroundImage && (
          <AnimatedDiv
            className="absolute inset-0 -z-10 h-full w-screen rounded-t-xl object-cover"
            style={INSET_ZERO}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Image
              src={backgroundImage}
              alt="배경 이미지"
              fill
              className="h-full w-screen rounded-t-xl object-cover"
            />
          </AnimatedDiv>
        )}
        {!isHeaderHidden && (
          <Div
            className={cn(
              'relative flex h-14 flex-row items-center justify-between gap-2 border-b border-b-white/5 p-4',
              !onPressClose && !onPressPrevious && !title && 'hidden',
              headerClassName,
            )}
          >
            {leftElement}
            {onPressClose && (isFullScreen || rightElement) && (
              <IconButton
                type="button"
                icon={Delete16Icon}
                onPress={onPressClose}
                className="p-0"
                iconClassName="size-6"
                hitSlop={16}
              />
            )}
            {onPressPrevious && (
              <IconButton
                type="button"
                icon={Previous16Icon}
                onPress={onPressPrevious}
                className="p-0"
                iconClassName="size-4"
                hitSlop={16}
              />
            )}
            <H3
              className={cn(
                'title16 absolute flex flex-row items-center justify-center',
                !checkIsNative() ? 'left-1/2 -translate-x-1/2' : '-z-10',
                titleClassName,
              )}
              style={
                checkIsNative() ? { left: 0, right: 0, top: 0, bottom: 0 } : {}
              }
            >
              {title}
            </H3>
            {onPressClose && !isFullScreen && !rightElement && (
              <IconButton
                type="button"
                icon={Delete16Icon}
                onPress={onPressClose}
                className="ml-auto p-0"
                iconClassName="size-4"
                hitSlop={16}
              />
            )}
            {rightElement}
          </Div>
        )}
        {modalContents}
      </AnimatedDiv>
    </TopComponent>
  );
};

export default memo(Modal);
